import { OpsItemStrategy, RuleType } from '../../../types/backend';
import type {
  OpsItemStrategyType,
  RuleTypeType,
  ItsmChannel,
  AutomationChannelRequest,
  NotificationTarget,
  ResolveSignal,
} from '../../../types/backend';
import type {
  EventRuleExtended,
  RuleTemplate,
  LiveEvent,
  TriggerType,
  SchedulePreset,
  EditorNotificationTarget,
} from '../../../types/frontend';
import { isWebhookTarget } from '../../../types/frontend';
import {
  DEFAULT_SCHEDULE_HOUR,
  DEFAULT_SCHEDULE_MINUTE,
  parseCronTime,
} from './scheduleUtils';
import { toEventEnvelope } from './eventDisplay';

export interface RuleFormState {
  ruleType: RuleTypeType;
  name: string;
  description: string;
  enabled: boolean;
  triggerType: TriggerType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventPattern: Record<string, any>;
  schedulePreset: SchedulePreset;
  scheduleHour: number;
  scheduleMinute: number;
  cronExpression: string;
  useCron: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  testEvent: Record<string, any>;
  // Per-channel C-02 delivery model
  enrichmentPromptId: string | null;
  summarize: boolean;
  itsm: ItsmChannel | null;
  notificationTargets: EditorNotificationTarget[];
  automation: AutomationChannelRequest | null;
  resolveSignal: ResolveSignal | null;
  opsitemStrategy: OpsItemStrategyType;
  dedupFieldPaths: string[];
}

/**
 * The backend rule template model stores itsm/automation/notification config
 * as opaque blobs (`itsm_config`, `automation_prompt_id`, `notifications`)
 * rather than the typed per-channel shapes rules use — there's no schema
 * enforcing they match `ItsmChannel`/`NotificationTarget`. The editor always
 * writes those shapes into the opaque fields (see TemplateEditor's
 * `handleSave`), so casting back on read is safe by convention, not by
 * contract.
 */
export function templateItsmConfigToChannel(itsmConfig: Record<string, unknown> | undefined): ItsmChannel | null {
  return (itsmConfig as ItsmChannel | undefined) ?? null;
}

export function templateNotificationsToTargets(notifications: Record<string, unknown>[]): NotificationTarget[] {
  return notifications as unknown as NotificationTarget[];
}

/**
 * The automation channel for `promptId`, keeping the agent the rule already names. A rule that
 * names none sends none, and the server stores the automation agent a Prompt Studio admin set
 * (PO 2026-10-28).
 */
export function automationChannel(promptId: string, current: AutomationChannelRequest | null): AutomationChannelRequest {
  return current?.agent_id ? { prompt_id: promptId, agent_id: current.agent_id } : { prompt_id: promptId };
}

/** A rule made from a template runs the agent the template names (ADR-0064 decision 13). */
export function templateAutomationPromptToChannel(
  promptId: string | undefined,
  agentId: string | undefined,
): AutomationChannelRequest | null {
  if (!promptId) return null;
  if (!agentId) throw new Error(`Template automation prompt '${promptId}' names no agent to run it`);
  return { prompt_id: promptId, agent_id: agentId };
}

export type FormAction =
  | { type: 'SET_FIELD'; field: keyof RuleFormState; value: unknown }
  | { type: 'RESET'; state: RuleFormState }
  | { type: 'SET_ENRICHMENT_PROMPT'; promptId: string | null }
  | { type: 'SET_SUMMARIZE'; value: boolean }
  | { type: 'SET_ITSM'; itsm: ItsmChannel | null }
  | { type: 'SET_AUTOMATION'; automation: AutomationChannelRequest | null }
  | { type: 'SET_RESOLVE_SIGNAL'; resolveSignal: ResolveSignal | null }
  | { type: 'ADD_NOTIFICATION_TARGET'; target: EditorNotificationTarget }
  | { type: 'REMOVE_NOTIFICATION_TARGET'; index: number }
  | { type: 'UPDATE_NOTIFICATION_TARGET'; index: number; target: EditorNotificationTarget };

export function formReducer(state: RuleFormState, action: FormAction): RuleFormState {
  switch (action.type) {
    case 'SET_FIELD': {
      const next = { ...state, [action.field]: action.value };
      if (action.field === 'ruleType') {
        const newType = action.value as RuleTypeType;
        if (newType === RuleType.AUTOMATION) {
          next.enrichmentPromptId = null;
          next.automation = next.automation ?? automationChannel('', null);
        }
      }
      if (action.field === 'opsitemStrategy' && action.value !== OpsItemStrategy.FINGERPRINT) {
        // dedup_field_paths is only valid with the fingerprint strategy. Clear it when
        // switching away so the save isn't a rejected (track + paths) combo — otherwise
        // the rule silently reverts to fingerprint and can't be moved off it.
        next.dedupFieldPaths = [];
      }
      return next;
    }
    case 'RESET':
      return action.state;
    case 'SET_ENRICHMENT_PROMPT':
      return { ...state, enrichmentPromptId: action.promptId };
    case 'SET_SUMMARIZE':
      return { ...state, summarize: action.value };
    case 'SET_ITSM':
      return { ...state, itsm: action.itsm };
    case 'SET_AUTOMATION': {
      // Derive rule_type from automation presence (D13)
      const newRuleType = action.automation !== null ? RuleType.AUTOMATION : RuleType.ALERT;
      return { ...state, automation: action.automation, ruleType: newRuleType };
    }
    case 'SET_RESOLVE_SIGNAL':
      return { ...state, resolveSignal: action.resolveSignal };
    case 'ADD_NOTIFICATION_TARGET':
      return { ...state, notificationTargets: [...state.notificationTargets, action.target] };
    case 'REMOVE_NOTIFICATION_TARGET':
      return {
        ...state,
        notificationTargets: state.notificationTargets.filter((_, i) => i !== action.index),
      };
    case 'UPDATE_NOTIFICATION_TARGET': {
      const notificationTargets = [...state.notificationTargets];
      notificationTargets[action.index] = action.target;
      return { ...state, notificationTargets };
    }
  }
}

function emptyForm(): RuleFormState {
  return {
    ruleType: RuleType.ALERT,
    name: '',
    description: '',
    enabled: true,
    triggerType: 'event',
    eventPattern: {},
    schedulePreset: 'monthly',
    scheduleHour: DEFAULT_SCHEDULE_HOUR,
    scheduleMinute: DEFAULT_SCHEDULE_MINUTE,
    cronExpression: '',
    useCron: false,
    enrichmentPromptId: '',
    summarize: true,
    itsm: null,
    notificationTargets: [],
    automation: null,
    resolveSignal: null,
    opsitemStrategy: OpsItemStrategy.TRACK,
    dedupFieldPaths: [],
    testEvent: {},
  };
}

// Convert an event detail body into an EventBridge pattern fragment: scalar
// leaves become single-value match arrays, nested objects recurse. Arrays of
// objects are skipped — they can't be expressed as a simple value match.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function detailToPattern(detail: Record<string, any>): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(detail)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      const scalars = value.filter((v) => v !== null && v !== undefined && typeof v !== 'object');
      if (scalars.length === value.length && scalars.length > 0) out[key] = scalars;
      continue;
    }
    if (typeof value === 'object') {
      const nested = detailToPattern(value);
      if (Object.keys(nested).length > 0) out[key] = nested;
      continue;
    }
    out[key] = [value];
  }
  return out;
}

export function buildInitialForm(
  rule: EventRuleExtended | null,
  template: RuleTemplate | null,
  fromEvent: LiveEvent | null,
): RuleFormState {
  if (rule) {
    const parsedTime = rule.schedule_expression ? parseCronTime(rule.schedule_expression) : null;
    return {
      ...emptyForm(),
      ruleType: rule.rule_type || RuleType.ALERT,
      name: rule.name,
      description: rule.description || '',
      enabled: rule.status === 'ENABLED',
      triggerType: rule.trigger_type || 'event',
      eventPattern: rule.event_pattern || {},
      schedulePreset: rule.schedule_preset || 'daily',
      scheduleHour: parsedTime?.hour ?? DEFAULT_SCHEDULE_HOUR,
      scheduleMinute: parsedTime?.minute ?? DEFAULT_SCHEDULE_MINUTE,
      cronExpression: rule.schedule_expression || '',
      useCron: !!rule.schedule_expression && !rule.schedule_preset,
      enrichmentPromptId: rule.enrichment_prompt_id ?? '',
      summarize: rule.summarize ?? true,
      itsm: rule.itsm ?? null,
      notificationTargets: rule.notification_targets ?? [],
      automation: rule.automation ?? null,
      resolveSignal: rule.resolve_signal ?? null,
      opsitemStrategy: rule.opsitem_strategy || OpsItemStrategy.TRACK,
      dedupFieldPaths: rule.dedup_field_paths ?? [],
      testEvent: rule.event_examples?.[0] || {},
    };
  }
  if (template) {
    const parsedTime = template.schedule_expression ? parseCronTime(template.schedule_expression) : null;
    return {
      ...emptyForm(),
      ruleType: (template.rule_type || RuleType.ALERT) as RuleTypeType,
      name: template.name || '',
      description: template.description,
      triggerType: (template.trigger_type || 'event') as TriggerType,
      eventPattern: template.event_pattern || {},
      scheduleHour: parsedTime?.hour ?? DEFAULT_SCHEDULE_HOUR,
      scheduleMinute: parsedTime?.minute ?? DEFAULT_SCHEDULE_MINUTE,
      cronExpression: template.schedule_expression || '',
      useCron: !!template.schedule_expression,
      enrichmentPromptId: template.enrichment_prompt_id ?? '',
      summarize: true,
      itsm: templateItsmConfigToChannel(template.itsm_config),
      notificationTargets: templateNotificationsToTargets(template.notifications),
      automation: templateAutomationPromptToChannel(template.automation_prompt_id, template.automation_agent_id),
      opsitemStrategy: (template.opsitem_strategy || OpsItemStrategy.TRACK) as OpsItemStrategyType,
    };
  }
  if (fromEvent) {
    const detailPattern = detailToPattern(fromEvent.payload || {});
    return {
      ...emptyForm(),
      description: `Rule for ${fromEvent.source} events`,
      eventPattern: {
        source: [fromEvent.source],
        'detail-type': [fromEvent.detail_type],
        ...(Object.keys(detailPattern).length > 0 ? { detail: detailPattern } : {}),
      },
      testEvent: toEventEnvelope(fromEvent),
    };
  }
  return emptyForm();
}

// Common "state" fields on alarm-style events and the value that means recovered.
// Used to one-click prefill a resolve signal from the rule's sample event.
const RECOVERY_HINTS: { path: string; recoveredValue: string }[] = [
  { path: 'detail.state.value', recoveredValue: 'OK' }, // CloudWatch Alarm State Change (EventBridge)
  { path: 'detail.NewStateValue', recoveredValue: 'OK' }, // CloudWatch alarm (legacy shape)
  { path: 'detail.status', recoveredValue: 'OK' },
];

function digPath(obj: Record<string, unknown>, dotted: string): unknown {
  let cur: unknown = obj;
  for (const part of dotted.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/**
 * Best-effort guess at a recovery signal from the sample event: if the event
 * carries a recognised alarm-state field, return a signal that resolves on 'OK'.
 * Returns null when nothing matches — the author then fills it in by hand.
 */
export function detectResolveSignal(
  sampleEvent: Record<string, unknown>,
): ResolveSignal | null {
  if (!sampleEvent || Object.keys(sampleEvent).length === 0) return null;
  for (const hint of RECOVERY_HINTS) {
    if (digPath(sampleEvent, hint.path) !== undefined) {
      return { field_path: hint.path, values: [hint.recoveredValue] };
    }
  }
  return null;
}

export const ITSM_CONTENT_KEYS = ['subject', 'description'] as const;
export const EMAIL_CONTENT_KEYS = ['subject', 'body'] as const;

const ENRICHMENT_ROOT = /^\$(\.{1,2}enrichment\b|\.{0,2}\[['"]enrichment['"]\])/;

/**
 * Mirrors the backend save check, per content key: with enrichment off each key needs a
 * non-blank mapping, and it cannot read the enrichment because there is none.
 */
function contentKeyErrors(
  mappings: Record<string, string> | undefined,
  keys: readonly string[],
  summarize: boolean,
): Record<string, string> {
  if (summarize) return {};
  const errors: Record<string, string> = {};
  for (const key of keys) {
    const value = (mappings?.[key] ?? '').trim();
    if (!value) errors[key] = 'Required with enrichment off';
    else if (ENRICHMENT_ROOT.test(value.replace(/\s/g, ''))) errors[key] = 'Enrichment is off, map this from the event';
  }
  return errors;
}

export function validateRuleForm(form: RuleFormState): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!form.name.trim()) {
    errors.name = 'Rule name is required';
  } else if (!/^[a-zA-Z0-9_-]+$/.test(form.name)) {
    errors.name = 'Only alphanumeric, underscores, and hyphens allowed';
  } else if (form.name.length > 64) {
    errors.name = 'Maximum 64 characters';
  }

  if (form.triggerType === 'event') {
    if (!form.eventPattern || Object.keys(form.eventPattern).length === 0) {
      errors.event_pattern = 'Event pattern is required';
    }
  } else {
    if (form.useCron && !form.cronExpression.trim()) {
      errors.schedule = 'Cron expression is required';
    }
  }

  if (form.itsm !== null) {
    if (!form.itsm.priority && !form.summarize) {
      errors.itsm_priority = 'Pick a priority: with enrichment off there is no severity';
    }
    for (const [key, message] of Object.entries(contentKeyErrors(form.itsm.field_mappings, ITSM_CONTENT_KEYS, form.summarize))) {
      errors[`itsm_mapping_${key}`] = message;
    }
  }

  if (form.opsitemStrategy === OpsItemStrategy.FINGERPRINT) {
    const paths = form.dedupFieldPaths.filter((p) => p.trim());
    if (paths.length === 0) {
      errors.dedup_field_paths = 'Add at least one field to fingerprint on';
    }
  }

  if (form.automation !== null && !form.automation.prompt_id) {
    errors.automation_prompt_id = 'Prompt is required for automation';
  }

  let hasWebhook = false;
  for (let i = 0; i < form.notificationTargets.length; i++) {
    const target = form.notificationTargets[i];
    if (isWebhookTarget(target)) {
      hasWebhook = true;
      if (!target.url.trim()) {
        errors[`notification_${i}_url`] = 'Webhook URL is required';
      }
      continue;
    }
    if (!target.to || target.to.length === 0) {
      errors[`notification_${i}_to`] =
        target.channel === 'sns' ? 'Topic ARN is required' : 'At least one email is required';
    }
    if (target.channel === 'email') {
      for (const [key, message] of Object.entries(contentKeyErrors(target.field_mappings, EMAIL_CONTENT_KEYS, form.summarize))) {
        errors[`notification_${i}_mapping_${key}`] = message;
      }
    }
  }

  // Webhook delivery has no backend yet — a rule carrying one cannot be saved.
  if (hasWebhook) {
    errors.webhook_unavailable =
      "Webhook delivery isn't available yet — remove the webhook to save (backend coming soon)";
  }

  if (form.resolveSignal) {
    if (!form.resolveSignal.field_path.trim()) {
      errors.resolve_field_path = 'Resolve field path is required';
    }
    if (form.resolveSignal.values.filter((v) => v.trim()).length === 0) {
      errors.resolve_values = 'Add at least one value that means recovered';
    }
  }

  return errors;
}
